import Image from "next/image";
import ExemptionsAnimationCloud from "public/account/exemptions/exemptions-animation-cloud.gif";
import ExemptionsEstimateCard from "../ExemptionsEstimateCard";
import { useExemptionsContext } from "../../ExemptionsContext";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import { usePropertyContext } from "../../context";
import ButtonV2 from "elements/ButtonV2";

const Intro = () => {
	const { next, flowId } = useExemptionsContext();
	const trackEvent = useEventTracker();
	const { details } = usePropertyContext();
	const handleSubmit = async () => {
		trackEvent({
			eventName: constants.EVENTS.Exemptions_Flow_Intro_Page_Next_Button,
			data: {
				"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
					? "Yes"
					: "No",
				State: details?.state,
			},
		});
		const trackingEventData = {
			State: details?.state,
		};
		next({ trackingEventData });
	};

	return (
		<div>
			<p className="lg bold">Let’s find you more savings!</p>
			<p className="sm">
				We're here to make the process of finding and filing{" "}
				{flowId === "NEW_YORK_EXEMPTION" ? "for a STAR Credit" : "exemptions"}{" "}
				hassle-free. Answer a few questions, we find you savings opportunities
				and we do all the work!
			</p>
			<ExemptionsEstimateCard />
			<ButtonV2
				size="large"
				id="account-property-profile-intro-begin"
				onClick={handleSubmit}>
				See How Much You Can Save
			</ButtonV2>

			<div className="exemptions-question-card mt-4">
				{flowId === "NEW_YORK_EXEMPTION" && (
					<div className="exemptions-question-card-white-card">
						<p className="bold">What’s a STAR Credit?</p>
						<p className="sm">
							STAR Credit is a program in New York that provides eligible
							homeowners with a reduction on their school property taxes through
							a yearly credit.
						</p>
					</div>
				)}
				{[
					"GEORGIA_EXEMPTION",
					"CALIFORNIA_EXEMPTION",
					"TEXAS_EXEMPTION",
					"ILLINOIS_EXEMPTION",
					"PENNSYLVANIA_EXEMPTION",
				].includes(flowId ?? "") && (
					<div className="exemptions-question-card-white-card mt-2 exemptions-intro-animation-box">
						<div>
							<p className="bold">How do exemptions work?</p>
							<p className="sm">
								Exemptions reduce the taxable value of your property. This means
								you're taxed on a lower amount, leading to a smaller tax bill
								every&nbsp;year.
							</p>
						</div>
						<div className="exemptions-intro-animation">
							<Image
								src={ExemptionsAnimationCloud}
								unoptimized
								alt="Loading animation"
								style={{
									maxWidth: "100%",
									height: "auto",
								}}
							/>
						</div>
					</div>
				)}
				{flowId === "FLORIDA_EXEMPTION" && (
					<>
						<div className="exemptions-question-card-white-card mt-2 exemptions-intro-animation-box">
							<div>
								<p className="bold">How do exemptions work?</p>
								<p className="sm">
									Exemptions and the Save Our Homes Assessment Limitation reduce
									the taxable value of your property. This means you're taxed on
									a lower amount, leading to a smaller tax bill every&nbsp;year.
								</p>
							</div>
							<div className="exemptions-intro-animation">
								<Image
									src={ExemptionsAnimationCloud}
									unoptimized
									alt="Loading animation"
									style={{
										maxWidth: "100%",
										height: "auto",
									}}
								/>
							</div>
						</div>
						<div className="exemptions-question-card-white-card mt-4 mb-2">
							<p className="bold">How much does this cost?</p>
							<p className="sm">
								With our contingency fee structure, we only charge ~
								{details?.contingency ?? 25}% of first year tax savings, which
								is roughly equal to $260. This means if you don't see any actual
								savings, we won't charge you anything.
							</p>
						</div>
					</>
				)}
				{flowId === "MICHIGAN_EXEMPTION" && (
					<>
						<div className="exemptions-question-card-white-card mt-2 exemptions-intro-animation-box">
							<div>
								<p className="bold">How do exemptions work?</p>
								<p className="sm">
									The Primary Residence Exemption exempts a residence from the
									tax levied by a local school district for school operating
									purposes. This means you're taxed on a lower amount, leading
									to a smaller tax bill every year.
								</p>
							</div>
							<div className="exemptions-intro-animation">
								<Image
									src={ExemptionsAnimationCloud}
									unoptimized
									alt="Loading animation"
									style={{
										maxWidth: "100%",
										height: "auto",
									}}
								/>
							</div>
						</div>
					</>
				)}
				{flowId === "CALIFORNIA_EXEMPTION" && (
					<div className="exemptions-question-card-white-card mt-4 mb-2">
						<p className="bold">How much does this cost?</p>
						<p className="sm">
							With our Savings-or-Free guarantee we only charge $18.37 which is
							roughly 35% of first year tax savings. If there are no savings, we
							will not charge you anything.
						</p>
					</div>
				)}
				{flowId === "GEORGIA_EXEMPTION" && (
					<div className="exemptions-question-card-white-card mt-4 mb-2">
						<p className="bold">How much does this cost?</p>
						<p className="sm">
							With our contingency fee structure, we only charge ~35% of first
							year tax savings equal to $175. If there are no savings, we will
							not charge you anything.
						</p>
					</div>
				)}
				{flowId === "PENNSYLVANIA_EXEMPTION" && (
					<div className="exemptions-question-card-white-card mt-4 mb-2">
						<p className="bold">How much does this cost?</p>
						<p className="sm">
							With our contingency fee structure, we only charge ~35% of first
							year tax savings equal to $185. This means if you don't see any
							actual savings, we won't charge you anything.
						</p>
					</div>
				)}
				<div className="exemptions-question-card-white-card mt-4 mb-2">
					<p className="bold">What you do</p>
					<p className="sm">
						Answer some easy questions, provide a document or two if needed,
						then sit back and relax!
					</p>
					<p className="bold mt-2">What we do</p>
					<ul>
						<li>
							<p className="sm">Identify available deduction opportunities</p>
						</li>
						<li>
							<p className="sm">Handle and file all paperwork</p>
						</li>
						<li>
							<p className="sm">
								Track refund status, amount and schedule if applicable
							</p>
						</li>
						<li>
							<p className="sm">
								Continue to monitor for new savings opportunities
							</p>
						</li>
					</ul>
				</div>
			</div>
			<p className="body-tiny exemptions-disclaimer-footer">
				*Actual tax savings may vary.
			</p>
		</div>
	);
};

export default Intro;
